import { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChild } from '../../contexts/ChildContext';
import { useDashboardSettings } from '../../hooks/useDashboardSettings';
import useDarkMode from '../../hooks/useDarkMode';
import { useFCMToken } from '../../hooks/useFCMToken';
import useTutorial from '../../hooks/useTutorial';
import { TimerProvider } from '../../contexts/TimerContext';
import { ViewerJournalPage } from '../../pages/PublicJournalPage';
import ChildSwitcherModal from '../ui/ChildSwitcherModal';
import TutorialTooltip from '../ui/TutorialTooltip';
import ActiveTimerBanner from '../ui/ActiveTimerBanner';
import UserProfileSetup from '../ui/UserProfileSetup';
import FirstChildSetup from '../ui/FirstChildSetup';
import QuickLogFAB from '../ui/QuickLogFAB';
import FeedbackModal from '../ui/FeedbackModal';
import Logo from '../ui/Logo';
import Sidebar from './Sidebar';
import BottomTabBar from './BottomTabBar';
import './layout.css';
import '../../styles/tutorial.css';
import '../../styles/timer.css';
import '../../styles/feedback.css';

/**
 * Nav section definitions — used to build Sidebar and BottomTabBar.
 * Each item maps a dashboardSettings section id to its route path.
 */
const NAV_SECTIONS = [
  { id: 'overview',          label: 'Overview',            icon: '🏠', path: '/app',                  alwaysVisible: true },
  { id: 'bottleFeeds',       label: 'Bottle Feeds',        icon: '🍼', path: '/app/bottle-feeds' },
  { id: 'breastfeeding',     label: 'Breast Feeding',      icon: '🤱', path: '/app/breast-feeding' },
  { id: 'solidFoods',        label: 'Solid Foods',         icon: '🥘', path: '/app/solid-foods' },
  { id: 'foodIntroductions', label: 'Food Introductions',  icon: '🍎', path: '/app/food-introductions' },
  { id: 'diapers',           label: 'Nappies',             icon: '🧷', path: '/app/diapers' },
  { id: 'sleepTracking',     label: 'Sleep',               icon: '😴', path: '/app/sleep' },
  { id: 'waterIntake',       label: 'Water Intake',        icon: '💧', path: '/app/water-intake' },
  { id: 'growthHealth',      label: 'Growth & Health',     icon: '📏', path: '/app/growth-health' },
  { id: 'milestones',        label: 'Milestones',          icon: '🎯', path: '/app/milestones' },
  { id: 'doctorVisits',      label: 'Doctor Visits',       icon: '🏥', path: '/app/doctor-visits' },
  { id: 'vaccinations',      label: 'Vaccinations',        icon: '💉', path: '/app/vaccinations' },
  { id: 'allergies',         label: 'Allergies',           icon: '⚠️', path: '/app/allergies' },
  { id: 'healthRecords',     label: 'Health Records',      icon: '📋', path: '/app/health-records' },
  { id: 'journal',           label: 'Journal',             icon: '📖', path: '/app/journal' },
  { id: 'timeline',          label: 'Timeline',            icon: '📅', path: '/app/timeline' },
];

/**
 * Onboarding tour steps.
 * targetSelector       — CSS selector matched against data-tutorial="…" attributes
 * mobileTargetSelector — used instead on viewports < 768px (sidebar is hidden on mobile)
 * position             — 'below' | 'above' | 'right' | 'center'
 * onActivate           — injected dynamically in AppLayout for steps that need state callbacks
 */
const TUTORIAL_STEPS = [
  {
    title: 'Welcome to Baby Tracker! 👶',
    body: "Let's take a quick tour so you know where everything is. Tap Next to continue, or Skip to dismiss.",
    targetSelector: '[data-tutorial="child-switcher"]',
    position: 'below',
  },
  {
    title: 'Switch between children',
    body: 'Tap this pill to add a new child, switch profiles, or manage who has access. Here\'s what it looks like:',
    targetSelector: '.child-switcher-modal',       // target the open modal card
    mobileTargetSelector: '.child-switcher-modal',
    position: 'below',
    // onActivate: opens ChildSwitcherModal — injected dynamically in AppLayout
  },
  {
    title: 'Navigate the app',
    body: 'Use the sidebar (desktop) or bottom tabs (mobile) to jump between tracking sections.',
    targetSelector: '[data-tutorial="sidebar-nav"]',
    mobileTargetSelector: '[data-tutorial="bottom-tab-bar"]',
    position: 'right',
  },
  {
    title: 'Log a bottle feed',
    body: 'Tap Bottle Feeds to start logging feeds — amount, time, and notes. Every section works the same way.',
    targetSelector: '[data-tutorial="nav-bottle-feeds"]',
    mobileTargetSelector: '[data-tutorial="bottom-tab-bottle"]',
    position: 'right',
  },
  {
    title: "Today's Summary",
    body: "Your dashboard shows a real-time snapshot of today's feeds, sleep, nappies, and more.",
    targetSelector: '[data-tutorial="summary-section"]',
    mobileTargetSelector: '[data-tutorial="summary-section"]',
    position: 'above',
  },
  {
    title: 'Customise your dashboard',
    body: "Hide sections you don't use or reorder them to match your routine.",
    targetSelector: '[data-tutorial="customise"]',
    mobileTargetSelector: '[data-tutorial="customise"]',
    position: 'below',
  },
  {
    title: 'Reminders 🔔',
    body: 'Set up feed and nappy reminders so you never miss a log — especially useful overnight.',
    targetSelector: '[data-tutorial="notifications"]',
    mobileTargetSelector: '[data-tutorial="notifications"]',
    position: 'below',
  },
  {
    title: 'Settings ⚙️',
    body: 'Manage your profile, notification reminders, dark mode, and dashboard layout — all in one place.',
    targetSelector: '[data-tutorial="settings-link"]',
    mobileTargetSelector: '[data-tutorial="settings-link-fab"]',
    position: 'right',
  },
];

/**
 * AppLayout — root layout wrapper for all authenticated routes.
 * Renders the sidebar (desktop), bottom tab bar (mobile), a top bar,
 * and an <Outlet> for the active page content.
 */
export default function AppLayout() {
  const { user, loading: authLoading, profile, profileLoading } = useAuth();
  const { selectedChild, childList, loading: loadingChildren } = useChild();
  const { sections: dashboardSections, tourCompleted, markTourComplete } = useDashboardSettings(user?.uid);
  const [isDark] = useDarkMode(); // eslint-disable-line no-unused-vars
  // Auto-registers the FCM token for this device whenever the user opens the app
  // with notification permission already granted. Token is stored in users/{uid}.fcmTokens.
  useFCMToken(user?.uid);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [childSwitcherOpen, setChildSwitcherOpen] = useState(false);
  const [feedbackOpen,     setFeedbackOpen]     = useState(false);

  const { active: tutorialActive, stepIndex, next: tutorialNext, skip: tutorialSkip, restart: tutorialRestart } =
    useTutorial(!!selectedChild, { tourCompleted, onComplete: markTourComplete });

  // Add/remove body class so CSS can boost overlapping modal z-indexes during the tour
  useEffect(() => {
    if (tutorialActive) document.body.classList.add('tutorial-active');
    else document.body.classList.remove('tutorial-active');
    return () => document.body.classList.remove('tutorial-active');
  }, [tutorialActive]);

  // Build steps with dynamic callbacks that need access to component state
  const tutorialSteps = useMemo(() => TUTORIAL_STEPS.map((step, i) => {
    if (i === 1) {
      // "Switch between children" — open the modal so the user sees it in action
      return { ...step, onActivate: () => setChildSwitcherOpen(true) };
    }
    // Close the modal when moving past that step
    if (i === 2) {
      return { ...step, onActivate: () => setChildSwitcherOpen(false) };
    }
    return step;
  }), []);

  // Apply gender theme to document root
  useEffect(() => {
    if (selectedChild?.gender) {
      const genderMap = { male: 'boy', female: 'girl', other: 'other' };
      document.documentElement.setAttribute(
        'data-gender',
        genderMap[selectedChild.gender] || selectedChild.gender
      );
    } else {
      document.documentElement.removeAttribute('data-gender');
    }
  }, [selectedChild]);

  // Single full-screen spinner covering both auth resolution and child profile loading.
  // Keeps the layout shell from rendering until we have a selected child, which means
  // DashboardPage always mounts with a real childId and its own data gate fires cleanly.
  if (authLoading || loadingChildren) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100" style={{ background: 'var(--bg-white)' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  // Redirect unauthenticated users only after auth state is confirmed
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // New user with no children yet — show the first-child setup flow
  if (!loadingChildren && childList.length === 0) {
    return <FirstChildSetup />;
  }

  // Viewer-only access: show read-only journal, not the full app
  if (selectedChild?.viewerOnly) {
    return <ViewerJournalPage child={selectedChild} />;
  }

  // Build visibility map from dashboardSettings
  const visibilityMap = Object.fromEntries(
    dashboardSections.map((s) => [s.id, s.visible])
  );

  // Build nav sections with visibility applied
  const navSections = [
    ...NAV_SECTIONS.map((section) => ({
      ...section,
      visible: section.alwaysVisible
        ? true
        : visibilityMap[section.id] !== false, // default visible if not in settings
    })),
    // Admin section — only injected for users with isAdmin: true on their profile
    ...(profile?.isAdmin ? [{
      id: 'admin',
      label: 'Admin',
      icon: '🛠️',
      path: '/admin/feedback',
      visible: true,
      alwaysVisible: true,
    }] : []),
  ];

  return (
    <TimerProvider childId={selectedChild?.id}>
    <div className="app-layout" data-testid="app-layout">
      {/* Desktop sidebar — conditionally rendered based on sidebarOpen */}
      {sidebarOpen && (
        <Sidebar sections={navSections} onRestartTutorial={tutorialRestart} onOpenFeedback={() => setFeedbackOpen(true)} />
      )}

      {/* Main content area */}
      <div className="app-main">
        {/* Top bar — brand (mobile), child name, dark mode, notifications */}
        <header className="app-top-bar" data-testid="app-top-bar">
            {/* Sidebar toggle — desktop only */}
          <button
            className="app-top-bar-btn d-none d-md-inline-flex"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            ☰
          </button>

          {/* Brand — visible on mobile where sidebar is hidden */}
          <NavLink to="/app" className="app-brand d-md-none" aria-label="Baby Tracker home">
            <Logo variant="full" size={26} />
          </NavLink>

          {/* Child switcher pill — opens modal, reachable from every page */}
          <div data-tutorial="child-switcher">
            <ChildSwitcherModal
              forceOpen={childSwitcherOpen}
              onForceClose={() => setChildSwitcherOpen(false)}
            />
          </div>

        </header>

        {/* Page content */}
        <main className="app-content" data-testid="app-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom tab bar — hidden on mobile (FAB handles nav) */}
      <BottomTabBar sections={navSections} />

      {/* Quick Log FAB — mobile only, hidden on desktop via CSS */}
      <QuickLogFAB navSections={navSections} onOpenFeedback={() => setFeedbackOpen(true)} />

      {/* Feedback modal — triggered from Sidebar (desktop) and FAB sheet (mobile) */}
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Active timer floating pill — visible from every page */}
      <ActiveTimerBanner />

      {/* User profile setup — shown to new users without a display name */}
      {!profileLoading && user && !profile?.displayName && !profile?.dismissed && (
        <UserProfileSetup onClose={() => {}} />
      )}

      {/* Onboarding tutorial — auto-starts once for new users */}
      {tutorialActive && (
        <TutorialTooltip
          step={tutorialSteps[stepIndex]}
          stepIndex={stepIndex}
          total={tutorialSteps.length}
          onNext={() => tutorialNext(tutorialSteps.length)}
          onSkip={tutorialSkip}
        />
      )}
    </div>
    </TimerProvider>
  );
}

// Export NAV_SECTIONS so tests and other components can use it
export { NAV_SECTIONS };
